import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      if (response.data.success && response.data.token) {
        sessionStorage.setItem('dashboard_auth', 'true');
        sessionStorage.setItem('dashboard_user', email);
        sessionStorage.setItem('dashboard_token', response.data.token);
        toast.success('Welcome back, Rutvi!');
        onLogin();
      } else {
        toast.error('Invalid email or password');
      }
    } catch (error) {
      toast.error('Invalid email or password');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F4F4F3] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back to website */}
        <a 
          href="/"
          className="flex items-center gap-2 text-[#78716C] hover:text-[#AA94FF] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Website
        </a>

        {/* Login Card */}
        <div className="bg-white rounded-3xl p-8 md:p-12 border border-[#1C1917]/5 shadow-xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_hyperlab-marketing/artifacts/bpjqoot1_EA48E728-648B-4FFC-984A-3BBF4550CA82.png"
              alt="HypeLab Marketing"
              className="h-12 mx-auto mb-4"
            />
            <h1 
              className="text-2xl font-bold text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Admin Dashboard
            </h1>
            <p className="text-sm text-[#78716C] mt-2">
              Sign in to access your inquiries
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-[#1C1917] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-[#F4F4F3] rounded-xl border border-[#AA94FF]/20 focus:border-[#AA94FF] focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-[#1C1917] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-12 pr-12 py-3 bg-[#F4F4F3] rounded-xl border border-[#AA94FF]/20 focus:border-[#AA94FF] focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#AA94FF] text-[#F4F4F3] rounded-full py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#CC5003] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Security Note */}
          <p className="text-xs text-[#78716C] text-center mt-6">
            This area is restricted to authorized personnel only.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
